import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Banner,
  ProgressBar,
  Icon,
} from '@shopify/polaris';
import { CheckCircleIcon } from '@shopify/polaris-icons';
import { useSites, useSyncSites } from '../hooks/useSites';
import { googleApi } from '../lib/api';

interface OnboardingStepProps {
  number: number;
  title: string;
  description: string;
  completed: boolean;
  active: boolean;
  children: React.ReactNode;
}

function OnboardingStep({
  number,
  title,
  description,
  completed,
  active,
  children,
}: OnboardingStepProps) {
  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack gap="300" blockAlign="start">
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: completed ? '#008060' : active ? '#5c6ac4' : '#e1e3e5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '14px',
              flexShrink: 0,
            }}
          >
            {completed ? <Icon source={CheckCircleIcon} tone="base" /> : number}
          </div>
          <BlockStack gap="100">
            <Text as="h3" variant="headingMd" fontWeight="semibold">
              {title}
            </Text>
            <Text as="p" tone="subdued" variant="bodySm">
              {description}
            </Text>
          </BlockStack>
        </InlineStack>

        <div style={{ marginLeft: '44px' }}>{children}</div>
      </BlockStack>
    </Card>
  );
}

interface SiteCardProps {
  id: string;
  name: string;
  hasGa4: boolean;
  onSetup: () => void;
}

function SiteCard({ name, hasGa4, onSetup }: SiteCardProps) {
  return (
    <div
      style={{
        padding: '16px',
        border: '1px solid #e1e3e5',
        borderRadius: '8px',
        backgroundColor: 'white',
      }}
    >
      <BlockStack gap="200">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="span" variant="bodyMd" fontWeight="semibold">
            {name}
          </Text>
          {hasGa4 && (
            <span style={{ color: '#008060', fontSize: '12px' }}>GA4 Connected</span>
          )}
        </InlineStack>
        <Button size="slim" onClick={onSetup}>
          {hasGa4 ? 'View Settings' : 'Setup GA4'}
        </Button>
      </BlockStack>
    </div>
  );
}

export function Onboarding() {
  const navigate = useNavigate();
  const { data: sites, isLoading: sitesLoading, refetch: refetchSites } = useSites();
  const syncMutation = useSyncSites();

  const [currentStep, setCurrentStep] = useState(1);

  // Calculate step completion
  const hasSites = sites && sites.length > 0;
  const hasGoogleConnectedSite = sites?.some((site) => site.google_connected);
  const hasConfiguredSite = sites?.some((site) => site.has_ga4_property);

  // Update current step based on completion
  useEffect(() => {
    if (hasConfiguredSite) {
      setCurrentStep(3);
    } else if (hasGoogleConnectedSite || hasSites) {
      setCurrentStep(hasSites ? 2 : 1);
    }
  }, [hasSites, hasGoogleConnectedSite, hasConfiguredSite]);

  const handleSyncSites = async () => {
    await syncMutation.mutateAsync();
    await refetchSites();
  };

  const handleComplete = () => {
    localStorage.setItem('onboarding_completed', 'true');
    navigate('/dashboard');
  };

  const handleSkip = () => {
    localStorage.setItem('onboarding_completed', 'true');
    navigate('/dashboard');
  };

  const progress = hasConfiguredSite ? 100 : hasGoogleConnectedSite ? 66 : hasSites ? 33 : 0;

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#f6f6f7',
        padding: '40px 20px',
      }}
    >
      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        <BlockStack gap="600">
          {/* Header */}
          <BlockStack gap="200">
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '16px',
                }}
              >
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    background:
                      'linear-gradient(135deg, #ff6b6b 0%, #feca57 50%, #48dbfb 100%)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span style={{ color: 'white', fontWeight: 'bold', fontSize: '24px' }}>
                    A
                  </span>
                </div>
              </div>
            </div>
            <Text as="h1" variant="headingXl" fontWeight="bold" alignment="center">
              Welcome to Analyzely!
            </Text>
            <Text as="p" tone="subdued" alignment="center">
              Let's get your GA4 analytics set up in 3 easy steps
            </Text>
          </BlockStack>

          {/* Progress Bar */}
          <Card>
            <BlockStack gap="200">
              <InlineStack align="space-between">
                <Text as="span" variant="bodySm" tone="subdued">
                  Setup Progress
                </Text>
                <Text as="span" variant="bodySm" fontWeight="semibold">
                  {Math.round(progress)}%
                </Text>
              </InlineStack>
              <ProgressBar progress={progress} size="small" tone="primary" />
            </BlockStack>
          </Card>

          {/* Step 1: Sync Sites */}
          <OnboardingStep
            number={1}
            title="Sync Your Webflow Sites"
            description="Import your Webflow sites to get started with analytics"
            completed={!!hasSites}
            active={currentStep === 1}
          >
            <BlockStack gap="300">
              <Button
                variant="primary"
                loading={syncMutation.isPending}
                onClick={handleSyncSites}
                disabled={sitesLoading}
              >
                {hasSites ? 'Refresh Sites' : 'Sync Sites'}
              </Button>

              {hasSites && (
                <Banner tone="success">
                  {sites.length} site{sites.length !== 1 ? 's' : ''} synced from Webflow!
                </Banner>
              )}

              {syncMutation.isError && (
                <Banner tone="critical">
                  Failed to sync sites. Please try again.
                </Banner>
              )}
            </BlockStack>
          </OnboardingStep>

          {/* Step 2: Connect Google */}
          <OnboardingStep
            number={2}
            title="Connect Google Analytics"
            description="Link your Google account to access GA4 properties"
            completed={!!hasGoogleConnectedSite}
            active={currentStep === 2 && !!hasSites}
          >
            <BlockStack gap="300">
              {hasSites ? (
                <>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Select a site below to connect Google Analytics:
                  </Text>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                      gap: '12px',
                    }}
                  >
                    {sites.slice(0, 3).map((site) => (
                      <div
                        key={site.id}
                        style={{
                          padding: '12px',
                          border: '1px solid #e1e3e5',
                          borderRadius: '8px',
                          backgroundColor: 'white',
                        }}
                      >
                        <BlockStack gap="200">
                          <InlineStack align="space-between" blockAlign="center">
                            <Text as="span" variant="bodySm" fontWeight="semibold">
                              {site.display_name}
                            </Text>
                            {site.google_connected && (
                              <Icon source={CheckCircleIcon} tone="success" />
                            )}
                          </InlineStack>
                          {!site.google_connected && (
                            <Button
                              size="slim"
                              onClick={() => {
                                window.location.href = googleApi.getConnectUrl(site.id);
                              }}
                            >
                              Connect Google
                            </Button>
                          )}
                        </BlockStack>
                      </div>
                    ))}
                  </div>
                  {sites.length > 3 && (
                    <Text as="p" variant="bodySm" tone="subdued">
                      +{sites.length - 3} more sites available in dashboard
                    </Text>
                  )}
                </>
              ) : (
                <Text as="p" variant="bodySm" tone="subdued">
                  Complete Step 1 first to see your sites
                </Text>
              )}
            </BlockStack>
          </OnboardingStep>

          {/* Step 3: Configure GA4 */}
          <OnboardingStep
            number={3}
            title="Select GA4 Property"
            description="Choose which GA4 property to use for each site"
            completed={!!hasConfiguredSite}
            active={currentStep === 3 && !!hasGoogleConnectedSite}
          >
            <BlockStack gap="300">
              {hasGoogleConnectedSite ? (
                <>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Configure GA4 for your connected sites:
                  </Text>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                      gap: '12px',
                    }}
                  >
                    {sites
                      ?.filter((site) => site.google_connected)
                      .slice(0, 3)
                      .map((site) => (
                        <SiteCard
                          key={site.id}
                          id={site.id}
                          name={site.display_name}
                          hasGa4={site.has_ga4_property}
                          onSetup={() => navigate(`/site/${site.id}/setup`)}
                        />
                      ))}
                  </div>
                </>
              ) : (
                <Text as="p" variant="bodySm" tone="subdued">
                  Complete Step 2 first to configure GA4
                </Text>
              )}
            </BlockStack>
          </OnboardingStep>

          {/* Success Banner */}
          {hasConfiguredSite && (
            <Banner tone="success">
              You're all set! Your analytics are now configured.
            </Banner>
          )}

          {/* Action Buttons */}
          <InlineStack align="center" gap="300">
            {hasConfiguredSite ? (
              <Button variant="primary" onClick={handleComplete}>
                Go to Dashboard
              </Button>
            ) : (
              <Button variant="plain" onClick={handleSkip}>
                Skip for now
              </Button>
            )}
          </InlineStack>
        </BlockStack>
      </div>
    </div>
  );
}
